import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowLeft, Trash2, ShoppingCart, Tag, CreditCard, Shield, CheckCircle } from 'lucide-react'
import { Button } from '@/clientWeb/components/ui/button'
import { Badge } from '@/clientWeb/components/ui/badge'
import { useTranslation } from '@/clientWeb/i18n/LanguageContext'

interface CartItem {
  id: string
  name: string
  description: string
  seats: number
  pricePerSeat: number
  period: 'monthly' | 'yearly'
  badge?: string
}

const INITIAL_CART: CartItem[] = [
  { id: 'pro', name: 'Pro Plan', description: 'Up to 300 participants, HD recording, full analytics', seats: 5, pricePerSeat: 15, period: 'monthly', badge: 'Popular' },
  { id: 'webinar', name: 'Webinar Add-on', description: 'Host webinars up to 1000 attendees', seats: 1, pricePerSeat: 29, period: 'monthly' },
]

export default function CartPage() {
  const t = useTranslation()
  const navigate = useNavigate()
  const [items, setItems] = useState<CartItem[]>(INITIAL_CART)
  const [coupon, setCoupon] = useState('')
  const [couponApplied, setCouponApplied] = useState(false)
  const [processing, setProcessing] = useState(false)

  const removeItem = (id: string) => setItems(prev => prev.filter(i => i.id !== id))
  const updateSeats = (id: string, delta: number) =>
    setItems(prev => prev.map(i => i.id === id ? { ...i, seats: Math.max(1, i.seats + delta) } : i))

  const subtotal = items.reduce((acc, i) => acc + i.seats * i.pricePerSeat, 0)
  const discount = couponApplied ? subtotal * 0.1 : 0
  const tax = (subtotal - discount) * 0.18
  const total = subtotal - discount + tax

  const handleCheckout = () => {
    setProcessing(true)
    setTimeout(() => {
      setProcessing(false)
      navigate('/settings/licenses/success')
    }, 2000)
  }

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      <div className="px-4 sm:px-6 py-3 border-b border-border bg-card shrink-0">
        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
          <button onClick={() => navigate('/settings/licenses')} className="hover:text-foreground flex items-center gap-1 transition-colors">
            <ArrowLeft className="w-3 h-3" />{t("licenses.title")}
          </button>
          <span>/</span><span className="text-foreground">{t("licenses.cart")}</span>
        </div>
        <div className="flex items-center gap-2">
          <ShoppingCart className="w-5 h-5 text-primary" />
          <h1 className="text-lg font-bold text-foreground">{t("licenses.yourCart")}</h1>
          <Badge variant="secondary" className="text-xs">{items.length} {t("licenses.items")}</Badge>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 sm:p-6">
        <div className="max-w-3xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Cart items */}
          <div className="lg:col-span-2 space-y-3">
            {items.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 gap-4 text-center">
                <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center">
                  <ShoppingCart className="w-8 h-8 text-muted-foreground" />
                </div>
                <p className="text-sm font-semibold text-foreground">{t("licenses.cartEmpty")}</p>
                <p className="text-xs text-muted-foreground">{t("licenses.addPlanToContinue")}</p>
                <Button onClick={() => navigate('/settings/licenses/pricing')} className="mt-2">{t("licenses.browsePlans")}</Button>
              </div>
            ) : (
              items.map((item, i) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ delay: i * 0.05 }}
                  className="bg-card border border-border rounded-2xl p-4"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-semibold text-foreground">{item.name}</p>
                        {item.badge && <Badge className="text-[10px] bg-primary/10 text-primary border-primary/20">{item.badge}</Badge>}
                      </div>
                      <p className="text-xs text-muted-foreground mb-3">{item.description}</p>
                      {/* Seat controls */}
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-muted-foreground">{t("licenses.seats")}:</span>
                        <div className="flex items-center gap-2">
                          <button onClick={() => updateSeats(item.id, -1)}
                            className="w-7 h-7 rounded-lg border border-border flex items-center justify-center text-sm font-bold hover:bg-muted transition-colors">−</button>
                          <span className="text-sm font-semibold text-foreground w-6 text-center">{item.seats}</span>
                          <button onClick={() => updateSeats(item.id, 1)}
                            className="w-7 h-7 rounded-lg border border-border flex items-center justify-center text-sm font-bold hover:bg-muted transition-colors">+</button>
                        </div>
                        <span className="text-xs text-muted-foreground">× ${item.pricePerSeat}/{item.period}</span>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2 shrink-0">
                      <p className="text-base font-bold text-foreground">${item.seats * item.pricePerSeat}</p>
                      <button onClick={() => removeItem(item.id)} className="text-muted-foreground hover:text-destructive transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))
            )}

            {items.length > 0 && (
              <button
                onClick={() => navigate('/settings/licenses/pricing')}
                className="text-xs text-primary hover:underline flex items-center gap-1"
              >
                {t("licenses.addMorePlans")}
              </button>
            )}
          </div>

          {/* Summary */}
          {items.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-3"
            >
              <div className="bg-card border border-border rounded-2xl p-4 space-y-4">
                <h3 className="text-sm font-semibold text-foreground">{t("licenses.orderSummary")}</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t("licenses.subtotal")}</span>
                    <span className="text-foreground font-medium">${subtotal.toFixed(2)}</span>
                  </div>
                  {couponApplied && (
                    <div className="flex justify-between text-green-600 dark:text-green-400">
                      <span>{t("licenses.discount")} (10%)</span>
                      <span>−${discount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t("licenses.tax")} (18%)</span>
                    <span className="text-foreground">${tax.toFixed(2)}</span>
                  </div>
                  <div className="border-t border-border pt-2 flex justify-between font-bold text-base">
                    <span className="text-foreground">{t("licenses.total")}</span>
                    <span className="text-foreground">${total.toFixed(2)}</span>
                  </div>
                </div>

                {/* Coupon */}
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Tag className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                      <input
                        value={coupon}
                        onChange={e => setCoupon(e.target.value)}
                        placeholder={t("licenses.couponCode")}
                        className="w-full pl-8 pr-3 py-2 text-xs border border-border rounded-xl bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                      />
                    </div>
                    <Button size="sm" variant="outline" className="text-xs shrink-0"
                      onClick={() => { if (coupon) setCouponApplied(true) }}>
                      {t("licenses.apply")}
                    </Button>
                  </div>
                  {couponApplied && (
                    <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                      className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" /> {t("licenses.discountApplied")}
                    </motion.p>
                  )}
                </div>

                <Button className="w-full gap-2" onClick={handleCheckout} disabled={processing}>
                  {processing ? (
                    <><span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />{t("licenses.processing")}</>
                  ) : (
                    <><CreditCard className="w-4 h-4" />{t("licenses.checkout")} ${total.toFixed(2)}</>
                  )}
                </Button>

                <div className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
                  <Shield className="w-3.5 h-3.5" /> {t("licenses.securePayment")}
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  )
}
